<script setup lang="ts">
import { useI18n } from 'vue-i18n'

/**
 * The three promises the whole site is built on. They are the design tenets stated
 * for visitors rather than for contributors, so keep them honest: if one stops
 * being true of the tools, it comes off this list.
 */
const PROPS = ['privacy', 'instant', 'languages'] as const

const { t } = useI18n()
</script>

<template>
  <section class="values" aria-labelledby="values-heading">
    <h2 id="values-heading">{{ t('home.values.heading') }}</h2>
    <dl class="values__list">
      <div v-for="key in PROPS" :key="key" class="values__item">
        <dt>
          <svg
            class="values__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <template v-if="key === 'privacy'">
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </template>
            <path v-else-if="key === 'instant'" d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />
            <template v-else>
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
            </template>
          </svg>
          {{ t(`home.values.${key}.title`) }}
        </dt>
        <dd>{{ t(`home.values.${key}.body`) }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped>
h2 {
  margin: 0 0 var(--space-4);
  font-size: 1.35rem;
}

.values__list {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  margin: 0;
}

.values__item {
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}

.values__item dt {
  font-weight: 600;
}

.values__icon {
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  margin-bottom: var(--space-3);
  color: var(--color-accent);
}

.values__item dd {
  margin: var(--space-2) 0 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
</style>
